import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Select,
  Statistic,
  Table,
  Alert,
  Progress,
  Collapse,
  Empty,
  Modal,
  Tag,
  Typography,
  Divider,
} from "antd";
import {
  SyncOutlined,
  TrophyOutlined,
  TeamOutlined,
  AlertOutlined,
  BarChartOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";

const CATEGORY_COLORS = {
  MDD: "#eb2f96",
  Sports: "#52c41a",
  Languages: "#1677ff",
  "Academic Clubs": "#722ed1",
  "Special Interest": "#fa8c16",
};

const CoCurricularDashboard = () => {
  const navigate = useNavigate();
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSummary, setStudentSummary] = useState(null);

  const buildDashboardUrl = useCallback(
    (studentID) => {
      const queryObj = {};
      if (filterAcadyrID) queryObj.acadyrID = filterAcadyrID;
      if (filterTermID) queryObj.termID = filterTermID;
      if (studentID) queryObj.studentID = studentID;
      const qs = new URLSearchParams(queryObj).toString();
      return `${domainSettings.urls.academics.coCurricularDashboard}${qs ? "?" + qs : ""}`;
    },
    [filterAcadyrID, filterTermID],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        buildDashboardUrl(),
        null,
        navigate,
      );
      if (response.ok) setDashboardData(response.data);
      else Utilities.showError(response, "Failed to load dashboard");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [navigate, buildDashboardUrl]);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
  }, [fetchAcademicYears, fetchTerms]);

  useEffect(() => {
    load();
  }, [load]);

  const participationByCategory = dashboardData?.participationByCategory || [];
  const awardsByTerm = dashboardData?.awardsByTerm || [];
  const participationBreakdown = dashboardData?.participationBreakdown || {};
  const activeOrOccasional =
    participationBreakdown.activeOrOccasionalStudents || [];
  const zeroActive = participationBreakdown.studentsWithZeroActiveRecords || [];
  const noRecords = participationBreakdown.studentsWithNoRecordsAtAll || [];

  const studentClassStream = (student) =>
    [student?.className, student?.streamName].filter(Boolean).join(" - ");

  const openStudentSummary = async (student) => {
    setSelectedStudent(student);
    setStudentSummary(null);
    setSummaryLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        buildDashboardUrl(student.id),
        null,
        navigate,
      );
      if (response.ok) {
        setStudentSummary(response.data?.studentSummaryContext || null);
      } else {
        Utilities.showError(response, "Failed to load student summary");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const renderStudentRows = (students) => (
    <div style={{ maxHeight: 240, overflowY: "auto", fontSize: 12 }}>
      {students.map((s) => (
        <Button
          key={s.id}
          type="text"
          block
          onClick={() => openStudentSummary(s)}
          style={{
            height: "auto",
            padding: "6px 0",
            borderBottom: "1px solid #f0f0f0",
            textAlign: "left",
          }}
        >
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Typography.Text strong>
              {s.fullName || `Student ${s.id}`}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {studentClassStream(s) || "Class and stream not set"}
            </Typography.Text>
          </Space>
        </Button>
      ))}
    </div>
  );

  const awardsColumns = [
    { title: "Term", dataIndex: "event__calendar__term__name", key: "term" },
    {
      title: "Awards",
      dataIndex: "awardsCount",
      key: "awardsCount",
      render: (v) => (
        <Space>
          <TrophyOutlined style={{ color: "#faad14" }} />
          <strong>{v}</strong>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h4 style={{ margin: 0 }}>
          <BarChartOutlined style={{ marginRight: 8 }} />
          Co-Curricular Dashboard
        </h4>
        <Space>
          <Select
            placeholder="Academic Year"
            allowClear
            style={{ width: 140 }}
            onChange={setFilterAcadyrID}
            options={academicYears.map((y) => ({
              value: y.id,
              label: y.acadyr,
            }))}
          />
          <Select
            placeholder="Term"
            allowClear
            style={{ width: 120 }}
            onChange={setFilterTermID}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Button
            icon={<SyncOutlined spin={loading} />}
            onClick={load}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Participation by Category */}
      <Card
        size="small"
        title={
          <Space>
            <TeamOutlined /> Participation by Activity Category
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {participationByCategory.length === 0 ? (
          <Empty description="No participation data yet" />
        ) : (
          <Row gutter={[16, 16]}>
            {participationByCategory.map((cat) => (
              <Col xs={24} sm={12} md={8} lg={6} key={cat.category}>
                <Card
                  size="small"
                  style={{
                    borderTop: `3px solid ${CATEGORY_COLORS[cat.category] || "#888"}`,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: CATEGORY_COLORS[cat.category] || "#888",
                    }}
                  >
                    {cat.categoryDisplay}
                  </div>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Statistic
                        title="Total"
                        value={cat.total}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Active"
                        value={cat.active}
                        valueStyle={{ fontSize: 18, color: "#52c41a" }}
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 4,
                        fontSize: 12,
                        color: "#888",
                      }}
                    >
                      <span>Participation Rate</span>
                      <span>{cat.participationRate}%</span>
                    </div>
                    <Progress
                      percent={cat.participationRate}
                      showInfo={false}
                      strokeColor={CATEGORY_COLORS[cat.category] || "#888"}
                      size="small"
                    />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Awards by Term */}
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Space>
                <TrophyOutlined /> Awards by Term
              </Space>
            }
            style={{ height: "100%" }}
          >
            {awardsByTerm.length === 0 ? (
              <Empty description="No awards recorded yet" />
            ) : (
              <Table
                columns={awardsColumns}
                dataSource={awardsByTerm}
                rowKey="event__calendar__term__name"
                size="small"
                pagination={false}
              />
            )}
          </Card>
        </Col>

        {/* Participation Breakdown */}
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Space>
                <AlertOutlined style={{ color: "#ff4d4f" }} /> Participation
                Breakdown
              </Space>
            }
            style={{ height: "100%" }}
          >
            <Collapse
              size="small"
              items={[
                {
                  key: "active-occasional",
                  label: (
                    <Space>
                      <span>Students who participated</span>
                      <strong style={{ color: "#52c41a" }}>
                        {activeOrOccasional.length}
                      </strong>
                    </Space>
                  ),
                  children:
                    activeOrOccasional.length === 0 ? (
                      <Alert
                        message="No active or occasional participation recorded yet"
                        type="info"
                        showIcon
                      />
                    ) : (
                      renderStudentRows(activeOrOccasional)
                    ),
                },
                {
                  key: "zero-active",
                  label: (
                    <Space>
                      <span>Students with zero active participation</span>
                      <strong style={{ color: "#ff4d4f" }}>
                        {zeroActive.length}
                      </strong>
                    </Space>
                  ),
                  children:
                    zeroActive.length === 0 ? (
                      <Alert
                        message="All students have active participation records"
                        type="success"
                        showIcon
                      />
                    ) : (
                      renderStudentRows(zeroActive)
                    ),
                },
                {
                  key: "no-records",
                  label: (
                    <Space>
                      <span>Students with no records at all</span>
                      <strong style={{ color: "#ff4d4f" }}>
                        {noRecords.length}
                      </strong>
                    </Space>
                  ),
                  children:
                    noRecords.length === 0 ? (
                      <Alert
                        message="All students have co-curricular records"
                        type="success"
                        showIcon
                      />
                    ) : (
                      renderStudentRows(noRecords)
                    ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={
          <Space>
            <UserOutlined />
            <span>{selectedStudent?.fullName || "Student Summary"}</span>
          </Space>
        }
        open={!!selectedStudent}
        onCancel={() => {
          setSelectedStudent(null);
          setStudentSummary(null);
        }}
        footer={null}
        width={760}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {selectedStudent && (
            <Space wrap>
              <Tag color="blue">
                {studentClassStream(selectedStudent) ||
                  "Class and stream not set"}
              </Tag>
              <Tag>Student ID: {selectedStudent.id}</Tag>
            </Space>
          )}
          {summaryLoading ? (
            <Alert
              message="Loading student co-curricular summary..."
              type="info"
              showIcon
            />
          ) : !studentSummary ? (
            <Empty description="No co-curricular summary available" />
          ) : (
            <>
              <Row gutter={12}>
                <Col span={12}>
                  <Statistic
                    title="Total Events"
                    value={studentSummary.totalEvents || 0}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Active Events"
                    value={studentSummary.activeEvents || 0}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
              </Row>
              <Divider style={{ margin: "8px 0" }} />
              <Table
                size="small"
                rowKey="participationID"
                pagination={false}
                dataSource={studentSummary.eventsBreakdown || []}
                locale={{
                  emptyText: "No event participation recorded for this student",
                }}
                columns={[
                  {
                    title: "Event",
                    dataIndex: "eventTitle",
                    key: "eventTitle",
                  },
                  {
                    title: "Category",
                    dataIndex: "eventCategory",
                    key: "eventCategory",
                  },
                  { title: "Term", dataIndex: "term", key: "term" },
                  {
                    title: "Involvement",
                    dataIndex: "involvementLevel",
                    key: "involvementLevel",
                    render: (value) => (
                      <Tag
                        color={value === "Non-participant" ? "red" : "green"}
                      >
                        {value}
                      </Tag>
                    ),
                  },
                  {
                    title: "Rating",
                    dataIndex: "performanceRating",
                    key: "performanceRating",
                    render: (value) => value || "-",
                  },
                  {
                    title: "Score",
                    dataIndex: "score",
                    key: "score",
                    render: (value) => value ?? "-",
                  },
                  {
                    title: "Award",
                    dataIndex: "awardTitle",
                    key: "awardTitle",
                    render: (value) => value || "-",
                  },
                ]}
              />
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default CoCurricularDashboard;
